import type { Card } from '@campfire/cards';
import type { HeartsEvent, HeartsView } from '@campfire/game-hearts';
import { pickTray } from '../cards.ts';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { trickCenter, trickHand } from './trick-table.ts';

const DIR_WORD = { left: 'to the left', right: 'to the right', across: 'across', hold: '' } as const;

function event(ctx: TableCtx, v: HeartsView): string {
  const e: HeartsEvent | null = v.last;
  const scored =
    e?.kind === 'hand' && v.firstTrick && v.trick.plays.length === 0
      ? `Hand ${e.hand}: ${e.points.map((p, s) => `${name(ctx, s)} ${p > 0 ? '+' : ''}${p}`).join(', ')}.${e.moon !== null ? ` ${name(ctx, e.moon)} shot the moon!` : ''} `
      : '';
  if (v.result) return scored.trim();
  if (v.phase === 'pass') return `${scored}Everyone passes 3 cards ${DIR_WORD[v.passDir]}.`;
  if (v.firstTrick && v.trick.plays.length === 0) return `${scored}${name(ctx, v.turn ?? 0)} ${v.turn === ctx.you ? 'lead' : 'leads'} the 2♣.`;
  return v.heartsBroken ? 'Hearts are broken.' : 'Hearts not broken yet.';
}

export const heartsUi: GameUi = {
  id: 'hearts',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as HeartsView;
    if (v.phase === 'pass') return `${v.scores[s] ?? 0} pts · ${v.passed[s] ? 'passed' : 'choosing'}`;
    return `${v.won[s] ?? 0} tricks · ${v.handPoints[s] ?? 0}♥ · ${v.scores[s] ?? 0} pts`;
  },
  render(ctx) {
    const v = ctx.msg.view as HeartsView;
    const hand = v.hand ?? [];
    const passing = v.phase === 'pass' && v.you !== null && !v.passed[v.you] && !v.result;
    const picked = (ctx.local.get<string[]>('hearts-pick') ?? []).filter((id) => hand.some((c) => c.id === id));
    const toggle = (c: Card) => ctx.local.set('hearts-pick', picked.includes(c.id) ? picked.filter((x) => x !== c.id) : picked.length < 3 ? [...picked, c.id] : picked);
    const pass = () => {
      ctx.local.set('hearts-pick', []);
      ctx.act({ type: 'pass', cards: picked });
    };
    const scores = h(
      'div.trick-scores',
      { 'data-testid': 'scores' },
      v.scores.map((total, s) =>
        h(
          `div.trick-score${v.turn === s ? '.turn' : ''}`,
          { 'data-testid': `score-${s}` },
          h('span.pname', {}, name(ctx, s)),
          h('span.big-num', {}, String(total)),
          h('span.muted', {}, v.phase === 'play' ? `+${v.handPoints[s] ?? 0} this hand · ${v.won[s] ?? 0} tricks` : ''),
        ),
      ),
    );
    return h(
      'div.trick-game.hearts',
      {},
      scores,
      v.phase === 'play'
        ? trickCenter(ctx, { trick: v.trick, lastTrick: v.lastTrick, seats: v.scores.length, turn: v.turn })
        : h('div.trick-center.passing', { 'data-testid': 'trick' }, h('div.pass-dir', {}, `Pass ${DIR_WORD[v.passDir]}`), h('div.muted', {}, `${v.passed.filter(Boolean).length} of ${v.passed.length} have passed`)),
      h('div.event', { 'data-testid': 'event' }, event(ctx, v)),
      passing
        ? h(
            'div.trick-controls',
            {},
            h('div.hint', { 'data-testid': 'hint' }, `Pick 3 cards to pass ${DIR_WORD[v.passDir]}.`),
            picked.length ? pickTray(picked.map((id) => hand.find((c) => c.id === id) as Card), (id) => ctx.local.set('hearts-pick', picked.filter((x) => x !== id)), () => ctx.local.set('hearts-pick', [])) : null,
            h('button.primary.big', { 'data-testid': 'hearts-pass', disabled: picked.length !== 3, onclick: pass }, `Pass ${picked.length}/3`),
          )
        : v.phase === 'pass' && v.yourPass
          ? h('div.hint', {}, 'Waiting for everyone to pass…')
          : ctx.myTurn
            ? h('div.hint', { 'data-testid': 'hint' }, v.firstTrick && v.trick.plays.length === 0 ? 'Lead the 2♣.' : 'Tap a highlighted card to play it.')
            : null,
      trickHand(ctx, v.hand, passing ? hand.map((c) => c.id) : v.legal, passing ? toggle : (c) => ctx.act({ type: 'play', card: c.id }), {
        picked: passing ? picked : v.yourPass ?? [],
        received: v.firstTrick ? v.received : [],
        dimOthers: !passing && ctx.myTurn,
      }),
      v.received.length && v.firstTrick ? h('div.muted.trick-foot', {}, 'Cards passed to you are marked.') : null,
      h('div.muted.trick-foot', {}, `Hand ${v.handNo} · game ends at ${v.target} · lowest score wins`),
    );
  },
};
